import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import cx from 'classnames';
import { ChooseService } from '../../../molecules/system/serviceRequestModal/chooseService/ChooseService';
import { ServiceForm } from '../../../molecules/system/serviceRequestModal/serviceForm/ServiceForm';
import { Typography } from '../../../atoms/typography/Typography';
import { Telephone } from '../../../../../static/icons/telephone';
import { Offer } from '../../../../../static/icons/offer';
import { Stack } from '../../../atoms/stack/Stack';
import { Demo } from '../../../../../static/icons/demo';
import ConfirmationModal from '../../../molecules/shared/confirmationModal/ConfirmationModal';
import ModalHeader from '../../../molecules/shared/modalHeader/ModalHeader';
import PrivacyCheckbox from '../../../molecules/shared/privacyCheckbox/PrivacyCheckbox';

const ServiceRequestModal = ({
  data,
  isOpen,
  onClose,
  forceType,
  extraFormData,
  t,
}) => {
  const types = [
    { id: 'DEMOREQUEST', icon: <Demo />, title: t('formCommon.demo') },
    { id: 'OFFERREQUEST', icon: <Offer />, title: t('formCommon.offer') },
    {
      id: 'MOREINFOREQUEST_CUSTOMER',
      icon: <Telephone />,
      title: t('formCommon.beContacted'),
    },
  ];

  const defaultStepState = forceType ? 2 : 1;
  const defaultSelectedType = forceType ? [forceType] : [];

  const [step, setStep] = useState(defaultStepState);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(defaultSelectedType);
  const [checkboxError, setCheckboxError] = useState(false);
  const [selected, setSelected] = useState(false);

  const handleCheckboxChange = (val) => {
    setSelected(!selected);
    if (checkboxError && val === 'on') {
      setCheckboxError(false);
    }
  };

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      license_number: '',
      text: '',
      type: '',
    },
  });

  const resetStates = () => {
    setStep(defaultStepState);
    setSelectedTypes(defaultSelectedType);
    setShowConfirmation(false);
    setCheckboxError(false);
    setSelected(false);
    methods.reset();
  };

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isOpen);
  }, [isOpen]);

  const handleNextStep = () => {
    setStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep((prevSteps) => prevSteps - 1);
    }
  };

  const handleClose = () => {
    setShowConfirmation((prev) => !prev);
  };

  const doClose = () => {
    resetStates();
    onClose();
  };

  const onTypeSelect = (type) => {
    // if (selectedTypes.find((el) => el === type)) {
    //   setSelectedTypes(selectedTypes.filter((el) => el !== type));
    //   return;
    // }
    // setSelectedTypes([...selectedTypes, type]);
    setSelectedTypes([type]);
  };

  const onSubmit = (submitData) => {
    data.handleSubmit({
      ...submitData,
      extraFormData,
      acceptAgree: selected,
      type: selectedTypes[0],
    });

    handleNextStep();
  };

  const disabledSubmit =
    !selected || Object.keys(methods.formState.errors).length > 0;

  const renderBottomButton = () => {
    switch (step) {
      case 1:
        return (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleNextStep();
            }}
            disabled={selectedTypes.length === 0}
            className={`w-full py-2 px-6 max-md:mt-3 bg-primary-700 text-clamp-ld uppercase font-medium text-white rounded-full global-hover ${
              selectedTypes.length === 0 && 'pointer-events-none bg-opacity-50'
            }`}
          >
            {t('formCommon.next')}
          </button>
        );
      case 2:
        return (
          <button
            type="submit"
            disabled={disabledSubmit}
            className={`w-full py-2 px-6 max-md:mt-3 mb-2 bg-primary-700 text-clamp-lg uppercase font-medium text-white rounded-full global-hover ${
              disabledSubmit && 'pointer-events-none bg-opacity-50'
            }`}
          >
            {t('formCommon.send')}
          </button>
        );
      case 3:
        return (
          <button
            type="button"
            onClick={doClose}
            className="w-full py-2 px-6 max-md:mt-3 bg-primary-700 text-clamp-lg uppercase font-medium text-white rounded-full global-hover"
          >
            {t('common.close')}
          </button>
        );
      default:
        break;
    }

    return null;
  };

  return (
    <>
      {isOpen && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FormProvider {...methods}>
          <div className="fixed top-0 left-0 w-full h-full p-0 sm:px-5 sm:py-10 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
            <div
              className={cx(
                `relative bg-white w-full pt-10 sm:pt-0 sm:w-5/6 max-w-3xl h-full sm:rounded-[40px] transition-opacity duration-500 opacity-100 pb-3`,
                {
                  'sm:max-h-[750px]':
                    step === 2 && selectedTypes[0] !== 'OFFERREQUEST',
                  'sm:max-h-[850px]':
                    step === 2 && selectedTypes[0] === 'OFFERREQUEST',
                  'sm:max-h-[450px]': step === 1,
                  'sm:max-h-[330px]': step === 3,
                }
              )}
            >
              <div className="overflow-y-auto h-full">
                <ModalHeader
                  className="mt-8"
                  imgUrl={data.guide.imgUrl}
                  imgAlt="Form guide"
                  onClose={handleClose}
                  title={data.guide.title}
                  subtitle={
                    data.guide.stepDescriptions[
                      `${step}-${selectedTypes[0]}`
                    ] || data.guide.stepDescriptions[step]
                  }
                />
                <form
                  id="ContactSystemSupplier"
                  className="flex flex-col justify-center py-3 md:py-5 px-6 gap-2"
                  onSubmit={methods.handleSubmit(onSubmit)}
                >
                  {step === 1 && (
                    <ChooseService
                      types={types}
                      selectedTypes={selectedTypes}
                      onTypeSelect={onTypeSelect}
                    />
                  )}
                  {step === 2 && (
                    <ServiceForm
                      types={types}
                      selectedTypes={selectedTypes}
                      choices={data.licenses}
                      handlePreviousStep={handlePreviousStep}
                      noBackButton={!!forceType}
                      t={t}
                    />
                  )}
                  {step === 3 && (
                    <Stack className="flex-1 justify-start">
                      <div className="flex justify-start bg-secondary-50 p-5 rounded-2xl">
                        <Typography textAlign="left" fontVariant="lg">
                          {data.successMessage}
                        </Typography>
                      </div>
                    </Stack>
                  )}
                  {renderBottomButton()}
                  {step === 2 && (
                    <PrivacyCheckbox
                      checked={selected}
                      onClick={handleCheckboxChange}
                      error={checkboxError}
                      privacyLink={data.urls.legalPrivacyPageUrl}
                      termsLink={data.urls.legalTermsPageUrl}
                      // required
                      t={t}
                    />
                  )}
                </form>
              </div>
              <div className="absolute top-0 sm:-top-8 flex justify-center w-full h-16 bg-transparent">
                <div className="flex flex-row min-w-40 h-full px-3 gap-3 bg-white shadow-md rounded-[40px] justify-center items-center">
                  {data.systemImgUrl && (
                    <img
                      className="w-[80px] h-[40px] rounded-full"
                      src={data.systemImgUrl}
                      alt={data.systemTitle}
                    />
                  )}
                  {!data?.systemImgUrl && data.systemTitle && (
                    <Typography
                      fontVariant="md"
                      className={`w-fit leading-none font-normal pr-6 ${
                        data.systemImgUrl ? '' : 'pl-6'
                      }`}
                    >
                      {data.systemTitle}
                      {extraFormData?.systemName &&
                        ` - ${extraFormData.systemName}`}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FormProvider>
      )}
      {showConfirmation && (
        <ConfirmationModal
          isOpen={showConfirmation}
          onConfirm={doClose}
          onCancel={handleClose}
          message={t('formCommon.confirmClose')}
          t={t}
        />
      )}
    </>
  );
};

export default ServiceRequestModal;
